import { useEffect, useState, useRef, useMemo } from 'react';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { generateMediaImageUrl } from '@jira/platform__generate-media-image-url/src/generateMediaImageUrl.tsx';
import type {
	ThemeSetting,
	MediaConfiguration,
} from '@jira/platform__project-theme-tokens/src/types.tsx';
import { isClientFetchError } from '@atlassian/jira-fetch/src/utils/is-error.tsx';
import { unsplashTracker } from '@jira/platform__unsplash/src/controllers/unsplash-tracker/index.tsx';

export const useBackgroundImageUrl = (
	themeSetting: ThemeSetting | null,
	media: MediaConfiguration | null | undefined,
): string | null => {
	const lowResUrl = useMemo(
		() =>
			media && themeSetting?.type === 'image'
				? generateMediaImageUrl({
						mediaClientId: media.clientId,
						mediaExternalEndpointUrl: media.externalEndpointUrl,
						mediaApiFileId: themeSetting.mediaApiFileId,
						token: themeSetting.mediaReadToken,
						mediaEndpoint: 'image',
						width: 1000,
						'max-age': Number.MAX_SAFE_INTEGER,
					})
				: null,
		[media, themeSetting],
	);

	const [imageUrl, setImageUrl] = useState<string | null>(lowResUrl);
	const previousThemeId = useRef<string | null>(null);
	const abortControllerRef = useRef<AbortController | null>(null);

	useEffect(() => {
		if (themeSetting?.type !== 'image') {
			previousThemeId.current = null;
			setImageUrl(null);
			return;
		}

		if (previousThemeId.current !== themeSetting.id && media) {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}

			abortControllerRef.current = new AbortController();

			previousThemeId.current = themeSetting.id;
			setImageUrl(lowResUrl);
			// build a new URL to the /binary path
			const binaryUrl = generateMediaImageUrl({
				mediaClientId: media.clientId,
				mediaExternalEndpointUrl: media.externalEndpointUrl,
				mediaApiFileId: themeSetting.mediaApiFileId,
				mediaEndpoint: 'binary',
				'max-age': Number.MAX_SAFE_INTEGER,
			});

			// send a request to fetch the image with the binary URL with bearer token authentication
			fetch(binaryUrl.toString(), {
				headers: {
					Authorization: `Bearer ${themeSetting.mediaReadToken}`,
				},
				signal: abortControllerRef.current?.signal,
			})
				.then((response) => response.blob())
				.then((blob) => {
					const objectUrl = URL.createObjectURL(blob);

					setImageUrl(objectUrl);

					// send viewing analytics to Unsplash for Unsplash images
					if (themeSetting.sourceType === 'UNSPLASH' && themeSetting.sourceIdentifier) {
						unsplashTracker.trackImage(themeSetting.sourceIdentifier);
					}
				})
				.catch((error) => {
					if (error.name === 'AbortError') {
						return;
					}
					// only fire error analytics if the error is not a client fetch error (e.g. network error, aborted)
					if (!isClientFetchError(error)) {
						fireErrorAnalytics({
							meta: {
								id: 'fetchBinaryBackgroundImage',
								packageName: 'jiraProjectThemeComponents',
								teamName: 'work-management-frontier',
							},
							error,
							sendToPrivacyUnsafeSplunk: true,
						});
					}
				});
		}

		return () => {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}
		};
	}, [themeSetting, media, lowResUrl]);

	return imageUrl;
};
